import { Banner, Spinner } from "@salesko/design-system";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Lock } from "lucide-react";
import { useI18n } from "../../i18n";
import { getBillingEntitlement } from "../../lib/api";
import { SettingsNote, SettingsSection } from "./SettingsSection";

export function BillingPanel({ enabled }: { enabled: boolean }) {
  const { t } = useI18n();
  const entitlementQuery = useQuery({
    queryKey: ["billing-entitlement"],
    queryFn: getBillingEntitlement,
    enabled,
    retry: 1,
    staleTime: 30_000
  });

  const messagingAddon = entitlementQuery.data?.messagingAddon ?? false;
  const subscription = entitlementQuery.data?.subscription ?? null;

  return (
    <SettingsSection
      ariaLabel={t.billing.ariaLabel}
      id="settings-billing"
      title={t.billing.title}
      trailing={entitlementQuery.isFetching ? <Spinner size={16} /> : <CreditCard size={15} />}
    >
      {!enabled ? <SettingsNote>{t.billing.signIn}</SettingsNote> : null}

      {enabled && messagingAddon ? (
        <Banner status="success">
          {t.billing.active}
          {subscription?.status === "pending_cancel" ? ` — ${t.billing.pendingCancel}` : ""}
          {subscription?.currentPeriodEnd
            ? ` (${t.billing.renews(new Date(subscription.currentPeriodEnd).toLocaleDateString())})`
            : ""}
          .
        </Banner>
      ) : null}

      {enabled && !messagingAddon ? (
        <>
          <SettingsNote tone="body">{t.billing.upsell}</SettingsNote>
          <div>
            {/* Stays an anchor on purpose: /pricing is a real navigable page,
                and the design-system Button only renders a <button>. */}
            <a
              href="/pricing"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                height: "var(--control-sm)",
                padding: "0 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-default)",
                background: "var(--surface-card)",
                boxShadow: "var(--shadow-xs)",
                color: "var(--text-strong)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-body-sm)",
                fontWeight: "var(--fw-bold)",
                textDecoration: "none"
              }}
            >
              <Lock size={14} />
              {t.billing.viewPlans}
            </a>
          </div>
        </>
      ) : null}
    </SettingsSection>
  );
}
